import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Grid,
  Card,
  FormHelperText,
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import { API_ENDPOINTS } from "../../../config/api";

const CreateProductForm = () => {
  const [formData, setFormData] = useState({
    productName: "",
    description: "",
    brand_id: "",
    category_id: "",
    product_photo: null,
    region_id: "",
    publish: false,
    price: "",
  });

  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [error, setError] = useState("");
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [preview, setPreview] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.BRAND_NAMES);
        setBrands(response.data.brands || response.data || []);
      } catch (error) {
        console.error("Failed to fetch brands:", error);
        setBrands([]);
        setSnackbar({
          open: true,
          message: "Failed to load brands.",
          severity: "error",
        });
      }
    };
    fetchBrands();
  }, []);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.REGION_NAMES);
        setRegions(response.data.regions || response.data || []);
      } catch (error) {
        console.error("Failed to fetch regions:", error);
        setRegions([]);
        setSnackbar({
          open: true,
          message: "Failed to load regions.",
          severity: "error",
        });
      }
    };
    fetchRegions();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.CATEGORY_NAMES);
        setCategories(response.data.categories || response.data || []);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        setCategories([]);
        setSnackbar({
          open: true,
          message: "Failed to load categories.",
          severity: "error",
        });
      }
    };
    fetchCategories();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : name === "price" ? (value === "" ? "" : Math.max(0, Number(value))) : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        setFormData((prev) => ({ ...prev, product_photo: null }));
        setPreview(null);
        setFileInputKey(Date.now());
        return;
      }
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");


        setFormData((prev) => ({ ...prev, product_photo: null }));
        setPreview(null);
        setFileInputKey(Date.now());
        return;
      }
      setError("");
      setFormData((prev) => ({ ...prev, product_photo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = () => {
    setFormData((prev) => ({ ...prev, product_photo: null }));
    setPreview(null);
    setError("");
    setFileInputKey(Date.now());
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Enhanced validation
    if (!formData.productName.trim()) {
      setSnackbar({ open: true, message: "Product Name is required", severity: "error" });
      return;
    }
    if (!formData.brand_id) {
      setSnackbar({ open: true, message: "Brand is required", severity: "error" });
      return;
    }
    if (!formData.product_photo) {
      setSnackbar({ open: true, message: "Product Photo is required", severity: "error" });
      return;
    }
    if (formData.price !== "" && isNaN(formData.price)) {
      setSnackbar({ open: true, message: "Price must be a valid number", severity: "error" });
      return;
    }

    const submitData = new FormData();
    submitData.append("product_name", formData.productName);
    submitData.append("description", formData.description);
    submitData.append("brand_id", formData.brand_id);
    submitData.append("category_id", formData.category_id);
    submitData.append("region_id", formData.region_id);
    submitData.append("price", formData.price);
    if (formData.product_photo instanceof File) {
      submitData.append("product_photo", formData.product_photo);
    }

    try {
      await axios.post(API_ENDPOINTS.PRODUCTS.CREATE, submitData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSnackbar({
        open: true,
        message: "Product created successfully!",
        severity: "success",
      });
      setTimeout(() => {
        navigate("/products-list");
      }, 1000);
    } catch (error) {
      console.error("Failed to create product:", error);
      let userMessage = "Failed to create product.";
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        if (errors.product_name) {
          userMessage = "A product with this name already exists. Please use a different name.";
        } else if (errors.brand_id) {
          userMessage = "Please select a valid brand.";
        } else if (errors.product_photo) {
          userMessage = "Please select a valid image file (max 5MB).";
        } else {
          userMessage = Object.values(errors).flat().join(", ");
        }
      } else if (
        error.response?.data?.message &&
        error.response.data.message.includes("foreign key constraint fails")
      ) {
        userMessage =
          "Cannot create this product because of related records. Please check your input and try again.";
      } else if (
        error.response?.data?.message &&
        error.response.data.message.includes("Duplicate entry")
      ) {
        userMessage = "A product with this name already exists. Please use a different name.";
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      }
      setSnackbar({
        open: true,
        message: userMessage,
        severity: "error",
      });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%", p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card sx={{ p: 3, width: "100%", boxShadow: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <input
                    id="product_photo"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    key={fileInputKey}
                    style={{ display: "none" }}
                  />
                  <label htmlFor="product_photo">
                    <Box
                      sx={{
                        border: "2px dashed #B0C4DE",
                        borderRadius: "10px",
                        padding: "20px",
                        textAlign: "center",
                        backgroundColor: "#F0F8FF",
                        cursor: "pointer",
                        minHeight: "120px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative",
                      }}
                    >
                      {preview ? (
                        <>
                          <img
                            src={preview}
                            alt="Product preview"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "80px",
                              marginBottom: "10px",
                              objectFit: "contain",
                            }}
                          />
                          <Typography variant="body1" sx={{ color: "#4682B4", fontWeight: "bold" }}>
                            {formData.product_photo?.name
                              ? formData.product_photo.name.replace(/</g, "&lt;").replace(/>/g, "&gt;")
                              : "Uploaded Image"}
                          </Typography>
                          <IconButton
                            onClick={handleRemoveFile}
                            sx={{
                              position: "absolute",
                              top: "5px",
                              right: "5px",
                              backgroundColor: "rgba(255, 255, 255, 0.8)",
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <UploadIcon
                            sx={{ fontSize: "40px", color: "#4682B4", marginBottom: "10px" }}
                          />
                          <Typography variant="body1" sx={{ color: "#4682B4", fontWeight: "bold" }}>
                            Upload Product Image
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#4682B4" }}>
                            Supports: JPG, JPEG, PNG
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                  {error && <FormHelperText error>{error}</FormHelperText>}
                  <FormHelperText className="text-center">
                    Upload Product Image (PNG, JPG, JPEG, max 5MB)
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <TextField
                    label="Product Name"
                    name="productName"
                    value={formData.productName}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    inputProps={{ maxLength: 100 }} // Prevent overly long input
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <TextField
                    type="number"
                    label="Price"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    inputProps={{ min: 0, step: "0.01" }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required disabled={brands.length === 0}>
                  <InputLabel id="brand-label">Brand</InputLabel>
                  <Select
                    labelId="brand-label"
                    name="brand_id"
                    value={formData.brand_id}
                    onChange={handleChange}
                    label="Brand"
                    required
                  >
                    <MenuItem value="">Select Brand</MenuItem>
                    {brands.length === 0 && (
                      <MenuItem value="" disabled>
                        No brands available
                      </MenuItem>
                    )}
                    {brands.map((brand) => (
                      <MenuItem key={brand.id || brand._id || brand.name} value={brand.id || brand._id}>
                        {brand.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={categories.length === 0}>
                  <InputLabel id="category-label">Category</InputLabel>
                  <Select
                    labelId="category-label"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    label="Category"
                  >
                    <MenuItem value="">Select Category</MenuItem>
                    {categories.length === 0 && (
                      <MenuItem value="" disabled>
                        No categories available
                      </MenuItem>
                    )}
                    {categories.map((category) => (
                      <MenuItem key={category.id || category._id || category.name} value={category.id || category._id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={12}>
                <FormControl fullWidth disabled={regions.length === 0}>
                  <InputLabel id="region-label">Region</InputLabel>
                  <Select
                    labelId="region-label"
                    name="region_id"
                    value={formData.region_id}
                    onChange={handleChange}
                    label="Region"
                  >
                    <MenuItem value="">Select Region</MenuItem>
                    {regions.length === 0 && (
                      <MenuItem value="" disabled>
                        No regions available
                      </MenuItem>
                    )}
                    {regions.map((region) => (
                      <MenuItem key={region.id || region._id || region.name} value={region.id || region._id}>
                        {region.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    variant="outlined"
                    inputProps={{ maxLength: 500 }} // Prevent overly long descriptions
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="publish"
                      checked={formData.publish}
                      onChange={handleChange}
                    />
                  }
                  label="Publish"
                />
              </Grid>

              <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  Create Product
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={() => navigate("/ecommerce/products-list")}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateProductForm;